import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { JournalEntry } from "@/types/journal";
import JournalEntryCard from "@/components/therapist/JournalEntryCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import ClientAvatar from "@/components/therapist/ClientAvatar";
import { getInitials, getAvatarColor } from "@/components/therapist/ClientUtils";
import { formatDate } from "@/utils/dateFormat";

const TherapistClientJournalEntries = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { clientId } = useParams<{ clientId: string }>();
  const [search, setSearch] = useState("");
  const [clientProfile, setClientProfile] = useState<any>(null);
  const [therapistNotes, setTherapistNotes] = useState<{ [entryId: string]: string }>({});

  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.replace("#", ""));
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("ring-4", "ring-blue-500", "ring-offset-2");
        setTimeout(() => {
          el.classList.remove("ring-4", "ring-blue-500", "ring-offset-2");
        }, 2000);
      }
    }
  }, [location]);

  // Fetch client profile info
  useEffect(() => {
    const fetchProfile = async () => {
      if (!clientId) return;
      const { data, error } = await supabase
        .from("user_profiles")
        .select("first_name, last_name, profile_image_url")
        .eq("user_id", clientId)
        .maybeSingle();
      if (!error && data) setClientProfile(data);
    };
    fetchProfile();
  }, [clientId]);

  // Fetch therapist notes
  useEffect(() => {
    const fetchTherapistNotes = async () => {
      if (!clientId) return;
      
      try {
        const { data: notes, error: notesError } = await supabase
          .rpc('get_therapist_notes_for_client', {
            client_id_param: clientId
          });
        
        if (!notesError && notes) {
          const notesMap: { [entryId: string]: string } = {};
          notes.forEach((note: any) => {
            notesMap[note.entry_id] = note.content;
          });
          
          setTherapistNotes(notesMap);
        }
      } catch (notesError) {
        console.error("Error fetching notes:", notesError);
      }
    };
    
    fetchTherapistNotes();
  }, [clientId]);

  const {
    data: entries,
    isLoading,
  } = useQuery({
    queryKey: ["therapist-client-journal-entries", clientId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("journal_entries")
        .select("*")
        .eq("user_id", clientId)
        .or("shareable_with_therapist.eq.true,shareable_with_therapist.is.null")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data || []) as JournalEntry[];
    },
    enabled: !!clientId,
  });

  const handleNoteChange = (entryId: string, content: string) => {
    setTherapistNotes(prev => ({ ...prev, [entryId]: content }));
  };

  if (isLoading) return <div className="p-8 text-center">Loading...</div>;
  if (!entries) return <div className="p-8 text-center">No entries found.</div>;

  const filteredEntries = entries.filter(
    (entry) =>
      entry.mood?.toLowerCase().includes(search.toLowerCase()) ||
      entry.content.toLowerCase().includes(search.toLowerCase()) ||
      (entry.emotion_type && entry.emotion_type.toLowerCase().includes(search.toLowerCase()))
  );

  const clientName = clientProfile ? `${clientProfile.first_name || ""} ${clientProfile.last_name || ""}`.trim() : "Client";
  const initials = getInitials(clientName);
  const avatarColor = getAvatarColor(clientName);
  const imageUrl = clientProfile?.profile_image_url || null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center gap-4 mb-2">
          <ClientAvatar imageUrl={imageUrl} name={clientName} initials={initials} avatarColor={avatarColor} />
          <h1 className="text-2xl font-bold text-gray-800">{clientName || "Client"}</h1>
        </div>
        <div className="flex justify-center mb-4">
          <Button
            variant="outline"
            onClick={() => navigate(-1)}
            className="w-full sm:w-auto"
          >
            Back
          </Button>
        </div>
        <div className="mb-6">
          <Input
            placeholder="Search entries by mood, content, or emotion..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="max-w-md"
          />
        </div>
        <div className="space-y-4">
          {filteredEntries.map(entry => (
            <JournalEntryCard
              key={entry.id}
              id={entry.id}
              entry={entry}
              clientId={clientId!}
              therapistNote={therapistNotes[entry.id] || ""}
              formatDate={formatDate}
              onNoteChange={handleNoteChange}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default TherapistClientJournalEntries;
